import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { sortBy } from 'lodash-es'

import { filterSelect } from 'syft-acp-util/components/FilterForm/FilterSelect'
import { setFilter } from 'syft-acp-core/store/filters/actions'
import { selectEmployerVenueOptionsById } from 'syft-acp-core/store/employerVenuesFiltered/selectors'
import { fetchEmployerFilteredVenuesList } from 'syft-acp-core/actions/employer-venues-filtered'

import type { EmployerBlocksVenueFilterProps } from './EmployerBlocksList.types'

/**
 * Scope filter for the employer Blocks tab: "Employer" (the default, all blocks
 * at the employer) or a specific venue. Selecting a venue sets `work_location_id`
 * in the URL query; selecting "Employer" clears it.
 *
 * Options come from the employer's own venue result set rather than the shared
 * entity map, which accumulates the venues of every employer visited this session.
 */
export const EmployerBlocksVenueFilter = ({ employerID }: EmployerBlocksVenueFilterProps) => {
  const dispatch = useDispatch()
  const venues = useSelector(state => selectEmployerVenueOptionsById(state, employerID))
  const value = useSelector(
    state => state.routing?.locationBeforeTransitions?.query?.work_location_id as string | undefined,
  )

  useEffect(() => {
    if (employerID) dispatch(fetchEmployerFilteredVenuesList(employerID))
  }, [dispatch, employerID])

  // filterSelect() builds a component, so it has to be memoised: a fresh component
  // type on every render remounts the select and closes an open dropdown.
  const FilterSelect = useMemo(
    () =>
      filterSelect(
        sortBy(
          venues.map(venue => ({ id: venue.id, label: venue.name })),
          ['label'],
        ),
        'Employer',
        'id',
        true,
      ),
    [venues],
  )

  const handleChange = (venueId: number | null) =>
    dispatch(setFilter('work_location_id', venueId === null ? null : String(venueId)))

  return (
    <FilterSelect
      allowAny
      name="work_location_id"
      value={value}
      ariaLabel="Filter blocks by venue"
      onChange={handleChange}
    />
  )
}
